import { Navigate } from 'react-router';

import {
  SessionLoadError,
  selectIsAuthenticated,
  selectNeedsOrgSetup,
  useSignOut,
  useSyncCurrentUser,
} from '@/features/auth';
import { useIdleTimer } from '@/lib/hooks/useIdleTimer';
import { PageSkeleton } from '@/lib/ui/molecules';
import { ROUTES } from '@/routes';
import { useAppSelector } from '@/store/hooks';

import { PublicLayout } from './PublicLayout';

const IDLE_TIMEOUT_MS = 120 * 60 * 1000; // * 2 hours.

/**
 * Wraps the org setup route: signed in, but no organization yet.
 *
 * Unauthenticated → /login. Already has an org (or is staff) → /dashboard.
 * Otherwise renders the auth-screen look from PublicLayout around the page.
 *
 * Re-checks /me on mount and on window focus, so a user whose org was assigned
 * by staff (the "contact support" path) gets through without signing in again.
 */
export function OrgSetupLayout() {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const needsOrgSetup = useAppSelector(selectNeedsOrgSetup);
  const signOut = useSignOut();
  const { isUserLoaded, isError, retry } = useSyncCurrentUser({ refetchOnFocus: true });

  useIdleTimer({
    timeout: IDLE_TIMEOUT_MS,
    onIdle: signOut,
  });

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.login} replace />;
  }

  if (!isUserLoaded) {
    return isError ? <SessionLoadError onRetry={retry} /> : <PageSkeleton />;
  }

  if (!needsOrgSetup) {
    return <Navigate to={ROUTES.dashboard} replace />;
  }

  return <PublicLayout />;
}
